import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTwitter, faTwitch, faYoutube, faInstagram, faTiktok } from '@fortawesome/free-brands-svg-icons';
import { faArrowLeft, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { Link, useLocation } from 'react-router-dom';

// Function to get widget data by URL path
const getWidgetDataByPath = (pathname) => {
  // Import widget data (we'll need to export this from HomePage)
  const widgetData = [
    {
      title: "@TrendingChess",
      subtitle: "live on twitter",
      icon: "/images/IndieChessMe.png",
      link: "https://twitter.com/TrendingChess",
      internalPageUrl: "/TrendingChess",
      description: "TrendingChess is dedicated to showcasing and promoting independent chess platforms, tools, and creators that prioritize community over corporate interests. We believe in supporting player-focused chess platforms, open-source tools, grassroots tournaments, and content creators who are building the future of chess outside of the major corporate ecosystems. Our mission is to create the definitive directory for indie chess resources where players can discover independent, community-driven chess platforms and creators.",
      contentFocus: [
        "Independent chess platform directory",
        "Community-driven chess resources",
        "Open-source chess tools promotion",
        "Grassroots tournament support",
        "Alternative chess platform discovery",
        "Player-focused chess communities"
      ],
      links: {
        twitter: "https://twitter.com/TrendingChess"
      }
    },
    {
      title: "Charlize van Zyl",
      subtitle: "presenter & influencer",
      icon: "/images/Charlize.png",
      link: "https://www.instagram.com/charlizevz/?hl=en",
      internalPageUrl: "/Charlize",
      description: "Professional chess presenter, influencer, and content creator bringing chess to life through engaging commentary, analysis, and educational content across multiple platforms. Known for her dynamic presentation style and deep understanding of the game, she brings chess to audiences worldwide through various digital platforms.",
      contentFocus: [
        "Chess tournament commentary",
        "Educational chess content",
        "Game analysis and breakdowns",
        "Chess community engagement",
        "Social media chess content",
        "Multi-platform chess broadcasting"
      ],
      links: {
        instagram: [
          "https://www.instagram.com/charlizevz/?hl=en",
          "https://www.instagram.com/checkmatecharlie/?hl=en"
        ],
        tiktok: "https://www.tiktok.com/@checkmate.charlie",
        youtube: "https://www.youtube.com/@Checkmate_CharlieYT",
        twitter: "https://x.com/Charlizevanzyl"
      }
    }
  ];

  return widgetData.find(widget => widget.internalPageUrl === pathname);
};

const CreatorPageTemplate = () => {
  const location = useLocation();
  const widgetData = getWidgetDataByPath(location.pathname);

  // Fallback to TrendingChess data if no match found
  const defaultData = getWidgetDataByPath('/TrendingChess');
  const data = widgetData || defaultData;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-gray-900 text-gray-100"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <Link 
          to="/" 
          className="flex items-center text-gray-400 hover:text-gray-100 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Home
        </Link>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="mb-6">
            <img 
              src={data.icon} 
              alt={data.title} 
              className="w-32 h-32 mx-auto rounded-3xl border-4 border-gray-600 object-cover"
            />
          </div>
          
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
            {data.title}
          </h1>
          
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            {data.subtitle}
          </p>
        </div>

        {/* About Section */}
        <div className="bg-gray-800 rounded-3xl p-8 mb-8 border border-gray-700">
          <h2 className="text-2xl font-bold mb-4 text-yellow-400">About {data.title.replace('@', '')}</h2>
          <div className="text-gray-300">
            <p>{data.description}</p>
          </div>
        </div>

        {/* Content Focus Section */}
        <div className="bg-gray-800 rounded-3xl p-8 mb-8 border border-gray-700">
          <h2 className="text-2xl font-bold mb-4 text-yellow-400">Content Focus</h2>
          <ul className="text-gray-300 space-y-2">
            {data.contentFocus.map((item, index) => (
              <li key={index}>• {item}</li>
            ))}
          </ul>
        </div>

        {/* Connect Section */}
        <div className="bg-gray-800 rounded-3xl p-8 border border-gray-700">
          <h2 className="text-2xl font-bold mb-6 text-yellow-400">Connect with {data.title.replace('@', '')}</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Dynamically render social links */}
            {data.links.twitter && (
              <motion.a
                href={data.links.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between bg-gray-700 p-6 rounded-2xl hover:bg-gray-600 transition-colors group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon 
                    icon={faTwitter} 
                    className="text-blue-400 text-2xl mr-4" 
                  />
                  <div>
                    <h3 className="font-bold text-white">Follow on Twitter</h3>
                    <p className="text-gray-400 text-sm">
                      {data.links.twitter.includes('@') ? data.links.twitter.split('/').pop() : `@${data.title.replace('@', '')}`}
                    </p>
                  </div>
                </div>
                <FontAwesomeIcon 
                  icon={faExternalLinkAlt} 
                  className="text-gray-400 group-hover:text-gray-200 transition-colors" 
                />
              </motion.a>
            )}

            {data.links.twitch && (
              <motion.a
                href={data.links.twitch}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between bg-gray-700 p-6 rounded-2xl hover:bg-gray-600 transition-colors group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon 
                    icon={faTwitch} 
                    className="text-purple-400 text-2xl mr-4" 
                  />
                  <div>
                    <h3 className="font-bold text-white">Watch on Twitch</h3>
                    <p className="text-gray-400 text-sm">{data.links.twitch.split('/').pop()}</p>
                  </div>
                </div>
                <FontAwesomeIcon 
                  icon={faExternalLinkAlt} 
                  className="text-gray-400 group-hover:text-gray-200 transition-colors" 
                />
              </motion.a>
            )}

            {data.links.youtube && (
              <motion.a
                href={data.links.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between bg-gray-700 p-6 rounded-2xl hover:bg-gray-600 transition-colors group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon 
                    icon={faYoutube} 
                    className="text-red-500 text-2xl mr-4" 
                  />
                  <div>
                    <h3 className="font-bold text-white">Watch on YouTube</h3>
                    <p className="text-gray-400 text-sm">{data.links.youtube.split('/').pop()}</p>
                  </div>
                </div>
                <FontAwesomeIcon 
                  icon={faExternalLinkAlt} 
                  className="text-gray-400 group-hover:text-gray-200 transition-colors" 
                />
              </motion.a>
            )}

            {data.links.instagram && (
              <motion.a
                href={Array.isArray(data.links.instagram) ? data.links.instagram[0] : data.links.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between bg-gray-700 p-6 rounded-2xl hover:bg-gray-600 transition-colors group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon 
                    icon={faInstagram} 
                    className="text-pink-400 text-2xl mr-4" 
                  />
                  <div>
                    <h3 className="font-bold text-white">Follow on Instagram</h3>
                    <p className="text-gray-400 text-sm">
                      {(Array.isArray(data.links.instagram) ? data.links.instagram[0] : data.links.instagram).split('/').pop()}
                    </p>
                  </div>
                </div>
                <FontAwesomeIcon 
                  icon={faExternalLinkAlt} 
                  className="text-gray-400 group-hover:text-gray-200 transition-colors" 
                />
              </motion.a>
            )}

            {data.links.tiktok && (
              <motion.a
                href={data.links.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between bg-gray-700 p-6 rounded-2xl hover:bg-gray-600 transition-colors group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon 
                    icon={faTiktok} 
                    className="text-white text-2xl mr-4" 
                  />
                  <div>
                    <h3 className="font-bold text-white">Follow on TikTok</h3>
                    <p className="text-gray-400 text-sm">{data.links.tiktok.split('/').pop()}</p>
                  </div>
                </div>
                <FontAwesomeIcon 
                  icon={faExternalLinkAlt} 
                  className="text-gray-400 group-hover:text-gray-200 transition-colors" 
                />
              </motion.a>
            )}
          </div>

          {/* Additional Instagram links for Charlize */}
          {data.links.instagram && Array.isArray(data.links.instagram) && data.links.instagram.length > 1 && (
            <div className="mt-6 text-center">
              <h3 className="text-lg font-semibold text-gray-200 mb-4">Additional Instagram Accounts</h3>
              <div className="flex flex-wrap justify-center gap-4">
                {data.links.instagram.slice(1).map((igLink, index) => (
                  <a
                    key={index}
                    href={igLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-pink-400 hover:text-pink-300 transition-colors"
                  >
                    @{igLink.split('/').pop()}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Additional Info */}
          <div className="mt-8 pt-6 border-t border-gray-600">
            <p className="text-gray-400 text-center">
              Stay connected and discover more content from {data.title.replace('@', '')}.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CreatorPageTemplate;